import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { CookieService } from 'ngx-cookie-service';
import {LocalStorageService} from 'angular-2-local-storage';
import {BehaviorSubject, from, Observable, Subject, throwError} from 'rxjs';
import {catchError, finalize, switchMap, tap} from 'rxjs/operators';
import {SessionStorageService} from 'ngx-store';
import {Config} from '../config';

declare var SJ: any;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  readonly cookieName = '.AspNet.AppCookie';
  readonly permissionsKey = 'app_permissions';
  readonly payloadKey = 'app_payload';
  readonly storageName = this.cookieName;
  private storedPath_: string = null;
  private disableSync = false;
  public get storedPath() {
    const path = this.storedPath_;
    this.storedPath_ = null;
    return path;
  }
  public set storedPath(v) {
    this.storedPath_ = v;
  }
  private authorized$$: BehaviorSubject<any> = new BehaviorSubject(this.isAuthorized());
  public get authorized$(): Observable<any> {
    return this.authorized$$.asObservable();
  }
  public get permissions(): number[]|null {
      if (!this.isAuthorized()) {
          return null;
      }
      let result = this.sessionStorageService.get(this.permissionsKey);
      if (!result) {
          result = this.ls.get(this.permissionsKey);
      }
      if (!result) {
          const res = this.cookie.get(this.permissionsKey);
          result = res ? JSON.parse(res) : null;
      }
      return result;
  }

  private _tfaRequest: ITfaRequest = null;
  public get tfaRequest(): ITfaRequest {
    return this._tfaRequest;
  }

  constructor(
      private api: ApiService,
      private cookie: CookieService,
      private ls: LocalStorageService,
      private sessionStorageService: SessionStorageService,
  ) {
    window.addEventListener('storage', (event) => {
      if (this.disableSync) {
        return;
      }
      if (!event.newValue) {
        return;
      }
      if (!this.isAuthorized()) {
        return;
      }
      switch (event.key) {
        case Config.lsPrefix + '.' + this.payloadKey:
          const newPayload: IAuthorizationPayload = JSON.parse(event.newValue);
          if (!newPayload) {
            return;
          }
          if (newPayload.login !== this.payload.login) {
            return;
          }
          this.sessionStorageService.set(this.payloadKey, newPayload);
          if (this.ls.get(this.payloadKey)) {
            this.ls.set(this.payloadKey, newPayload);
          }
          if (this.cookie.get(this.payloadKey)) {
            this.cookie.set(this.payloadKey, JSON.stringify(event.newValue));
          }
          break;
      }
    });
  }

  public login(request: ILoginRequest, savePassword = true): Observable<IAuthorizationResponse> {
    return this.loginHandler(this.api.post('login', request), request, savePassword);
  }

  public logout(): Observable<any> {
    this.disableSync = true;
    return new Observable((observer) => {
        observer.next(true);
        observer.complete();
    }).pipe(
        tap((response) => {
          this.sessionStorageService.set(this.storageName, null);
          this.sessionStorageService.set(this.permissionsKey, null);
          this.sessionStorageService.set(this.payloadKey, null);
          this.cookie.delete(this.cookieName);
          this.cookie.delete(this.storageName);
          this.cookie.delete(this.permissionsKey);
          this.cookie.delete(this.payloadKey);
          this.ls.set(this.storageName, null);
          this.ls.set(this.permissionsKey, null);
          this.ls.set(this.payloadKey, null);
          this.authorized$$.next(false);
          this.disableSync = false;
        })
    );
  }

  public unlockedRefresh(): Observable<any> {
      return this.api.post('renew').pipe(
          tap((response) => {
              const payload = this.payload;
              payload.token = response.Token;
              payload.refreshToken = response.RefreshToken;
              this.handleAuthResponse(response, payload, !!this.ls.get(this.storageName));
          }),
          catchError( err => {
              return throwError(err);
          })
      );
  }

  public refresh(): Observable<any> {
    let renewLock;
    const p = new Promise<void>((resolve) => {
       renewLock = SJ.lock('bm.renew', () => resolve());
    });
    return from(p).pipe(
      switchMap(() => this.unlockedRefresh().pipe(
        finalize(() => renewLock.release()),
      )),
    );
  }

  public saveConsent(id: number): Observable<any> {
      return this.api.post('consent/' + String(id)).pipe(
          tap(() => {
              const payload = this.payload;
              payload.consentIds.push(id);
              this.updatePayload(payload);
          })
      );
  }

  public isAuthorized(): boolean {
    return !!this.cookie.get(this.cookieName) || !!this.ls.get(this.storageName) || !!this.sessionStorageService.get(this.storageName);
  }

  public get payload(): IAuthorizationPayload|null {
    if (!this.isAuthorized()) {
        return null;
    }
    let result = this.sessionStorageService.get(this.payloadKey);
    if (!result) {
        result = this.ls.get(this.payloadKey);
    }
    if (!result) {
        const res = this.cookie.get(this.payloadKey);
        result = res ? JSON.parse(res) : null;
    }
    return result;
  }

  public hasConsent(id: number): boolean {
    const payload = this.payload;
    if (!payload || !payload.consentIds) {
        return false;
    }
    try {
        return payload.consentIds.indexOf(id) >= 0;
    } catch (e) {
        return false;
    }
  }

  public hasActionRequiredParameter(): boolean {
    const payload = this.payload;
    if (!payload || !payload.parameters) {
      return false;
    }

    try {
      return payload.parameters.ActionRequiredParameter === ActionRequiredPageParameter.ActionsWithLotInfo;
    } catch (e) {
      return false;
    }
  }

  public checkSignupCode(code: string): Observable<string> {
    return this.api.get('signup/' + code);
  }

  public signup(request: ISignupRequest): Observable<IAuthorizationResponse> {
    return this.loginHandler(this.api.post('signup', request), request);
  }

  public generateCode(): Observable<string> {
    return this.api.get('generate-code');
  }

  public resetEmail(login: string): Observable<string> {
    return this.api.post('reset-email', {login});
  }

  public checkResetEmailCode(code: string): Observable<string> {
    return this.api.get('reset-email/' + code);
  }

  public checkResetPasswordCode(code: string): Observable<string> {
    return this.api.get('reset-password/' + code);
  }

  public resetPassword(request: IResetPasswordRequest, login: string): Observable<IAuthorizationResponse> {
    return this.loginHandler(this.api.post('reset-password', request), {
      Login: login,
      Password: request.Password
    });
  }

  private handleAuthResponse(response: any, payload: IAuthorizationPayload, savePassword = true) {
    this.sessionStorageService.set(this.storageName, true);
      this.sessionStorageService.set(this.permissionsKey, response.Permissions);
      this.sessionStorageService.set(this.payloadKey, payload);
      this.sessionStorageService.set('language56', response.Parameters.Language.label);
      if (savePassword) {
          this.ls.set(this.storageName, true);
          this.ls.set(this.permissionsKey, response.Permissions);
          this.ls.set(this.payloadKey, payload);
          this.ls.set('language', response.Parameters.Language.Label);
          this.cookie.set(this.storageName, '1');
          this.cookie.set(this.permissionsKey, JSON.stringify(response.Permissions));
          this.cookie.set(this.payloadKey, JSON.stringify(payload));
      }
  }

  private updatePayload(payload: IAuthorizationPayload) {
      this.sessionStorageService.set(this.payloadKey, payload);
      if (this.ls.get(this.payloadKey)) {
          this.ls.set(this.payloadKey, payload);
          this.cookie.set(this.payloadKey, JSON.stringify(payload));
      }
  }

  private loginHandler(query: Observable<any>, request: ILoginRequest, savePassword = true): Observable<IAuthorizationResponse> {
    return query.pipe(
      tap((response: IAuthorizationResponse) => {
        if (response.RequiresTwoFactor) {
          this._tfaRequest = {
            Login: request.Login,
            Password: request.Password,
            RequestTime: new Date(),
          };
        }
        if (!response.Success || response.RequiresTwoFactor) {
          return;
        }
        const payload: IAuthorizationPayload = {
          login: request.Login,
          token: response.Token,
          refreshToken: response.RefreshToken
        };
        try {
          payload.consentIds = response.ConsentIds.map(e => Number(e));
        } catch (e) {
          payload.consentIds = [];
        }

        payload.parameters = response.Parameters;

        this.handleAuthResponse(response, payload, savePassword);
        this.authorized$$.next(this.isAuthorized());
      }),
      catchError( err => {
        return throwError(err);
      })
    );
  }
}
